<template>
  <ul>
    <!-- Dashboard -->
    <SidebarMenuItem
      :item-links="[{ title: 'Dashboard', url: '/dashboard/home' }]"
      icon="DashboardIcon"
      :drop-right="sideBarMenuList['dashboard']"
      @toggle-menu="handleSidebarDropDown('dashboard', $event)"
    />
    <SidebarMenuItem
      :item-links="menuItems"
      menu-title="Data Configuration"
      icon="EarthSettingIcon"
      :drop-right="sideBarMenuList['configuration']"
      @toggle-menu="handleSidebarDropDown('configuration', $event)"
    />

    <!-- Data Upload Page -->
    <SidebarMenuItem
      :item-links="dataUploadButtons"
      menu-title="Measure"
      icon="MeasureIcon"
      :drop-right="sideBarMenuList['measure']"
      @toggle-menu="handleSidebarDropDown('measure', $event)"
    />

    <!-- Report -->
    <SidebarMenuItem
      :item-links="reportingLinks"
      menu-title="Report"
      icon="ReportIcon"
      :drop-right="sideBarMenuList['report']"
      @toggle-menu="handleSidebarDropDown('report', $event)"
    />

    <!-- Decarbonization -->
    <SidebarMenuItem
      :item-links="[
        { title: 'Target Setting', url: '/dashboard/ghg-overview' },
        { title: 'Action Plan', url: '/dashboard/abatement/' },
      ]"
      menu-title="Optimize"
      icon="OptimizeIcon"
      :drop-right="sideBarMenuList['optimize']"
      @toggle-menu="handleSidebarDropDown('optimize', $event)"
    />

    <!-- Supplier Portal -->
    <div
      v-if="isSupplierPortalEnabled()"
      @mouseover="dropRightSupplierPortal = true"
      @mouseleave="dropRightSupplierPortal = false"
    >
      <li class="rounded-sm relative mb-0.5 last:mb-0 select-none">
        <a
          href="https://app.powerbi.com/Redirect?action=OpenApp&appId=fae875ca-799b-43c0-a0f8-a0778f87c61d&ctid=f4ea409d-d896-4e1e-9412-8dc02a49613a"
          target="_blank"
          class="relative z-10 block truncate transition duration-150 text-white/60 hover:text-white"
          title="Supplier Portal"
        >
          <div
            class="flex items-center py-3 md:py-3"
            :class="{ 'p-0 justify-center': !sideBarActive, 'md:px-3 md:py-3 lg:px-3': sideBarActive }"
          >
            <div
              class="flex items-center menu-inner"
              :class="{ 'mx-auto': !sideBarActive, 'm-0': sideBarActive }"
            >
              <component
                :is="'LayerPlus'"
                class="w-5 h-5"
                :class="{ 'mr-0': !sideBarActive, 'mr-3': sideBarActive }"
              />
              <span
                class="hidden ml-1 text-sm font-medium duration-200 lg:sidebar-expanded:opacity-100 2xl:opacity-100"
                :class="{ 'md:hidden lg:block': sideBarActive }"
              >Supplier Portal</span>
            </div>
          </div>
        </a>
        <!-- Dropright Menu -->
        <div
          v-if="dropRightSupplierPortal"
          class="absolute z-50 left-8 md:left-12 lg:left-11 z-9 top-0 min-w-[220px] bg-gray-200 flex flex-col"
          :class="{ '!hidden': sideBarActive }"
        >
          <div class="flex flex-col p-3 bg-sidebar-green-primary">
            <a
              href="https://app.powerbi.com/Redirect?action=OpenApp&appId=fae875ca-799b-43c0-a0f8-a0778f87c61d&ctid=f4ea409d-d896-4e1e-9412-8dc02a49613a"
              class="text-sm text-white/60"
            >
              <span>Supplier Portal</span>
            </a>
          </div>
        </div>
      </li>
    </div>

    <!-- Settings -->
    <SidebarMenuItem
      :item-links="[
        {
          title: 'My Account',
          url: '/dashboard/settings/general',
          hidden: false,
        },
        {
          title: 'My Companies',
          url: '/dashboard/companies',
          hidden: !canViewCompanies,
        },
        {
          title: 'Users & Invites',
          url: '/dashboard/settings/users',
          hidden: isManageUsersEnabled() || !isCompanyAdmin,
        },
        {
          title: 'User Permissions',
          url: '/dashboard/settings/permissions',
          hidden: true,
        },
        {
          title: 'Manage Users',
          url: '/dashboard/settings/manage-users',
          hidden: !isManageUsersEnabled() || !isCompanyAdminOrUserManager,
        },
      ]"
      menu-title="Settings"
      icon="CogIcon"
      :drop-right="sideBarMenuList['setting']"
      @toggle-menu="handleSidebarDropDown('setting', $event)"
    />
  </ul>
</template>

<script setup lang="ts">
import { UserAccountRoles } from '~/constants/userRoles'

const {
  isSupplierPortalEnabled,
  isEsgRevampedEnabled,
  isEsgSubmissionRevampEnabled,
  isGhgAssignmentRevampEnabled,
  isNewUserAssignmentEnabled,
  isManageUsersEnabled,
  isReportingSideAppEnabled,
} = useFeatureFlag()

const dropRightSupplierPortal = ref<boolean>(false)

const sideBarMenuList = reactive<Record<string, boolean>>({
  dashboard: false,
  configuration: false,
  measure: false,
  setting: false,
  report: false,
  optimize: false,
})

const sideBarActive = computed(() => {
  return useNuxtApp().$store.sidebar.responsiveActive
})

const dataUploadButtons = computed(() => {
  const items = []

  if (isEsgSubmissionRevampEnabled()) {
    items.push({
      title: 'ESG Data Entry',
      url: '/dashboard/esg-data-entry',
      hidden: !canViewEsgDataEntry.value,
    })
    items.push({
      title: 'ESG Data Submissions',
      url: '/dashboard/esg-data-submission',
    })
  }
  else {
    items.push({
      title: 'ESG Data Entry',
      url: '/dashboard/data-entry/esg-data-entry',
      hidden: !canViewEsgDataEntry.value,
    })
  }

  items.push({
    title: canViewGhgDataEntry.value ? 'GHG Data Entry' : 'GHG Data Submissions',
    url: canViewGhgDataEntry.value ? '/dashboard/data-upload' : '/dashboard/data-upload/submissions',
  })
  items.push({
    title: 'GHG Progress Tracker',
    url: '/dashboard/ghg-progress-tracker',
    hidden: !canViewGhgProgressTracker.value,
  })
  items.push({
    title: 'GHG Data Approval',
    url: '/dashboard/ghg-approval',
    hidden: !canViewGhgDataApproval.value,
  })

  return [
    ...items,
    { title: 'Emission Audit Log', url: '/dashboard/emission-audit-log' },
    {
      title: 'My Assignments',
      url: '/dashboard/assignments',
      hidden: !canViewMyAssignments.value,
    },
  ]
})

const isCompanyAdmin = computed(() => {
  const { auth, companies, userSettings } = useNuxtApp().$store

  if (isManageUsersEnabled()) {
    const userRoles = userSettings.userRoles
    return userRoles.includes(UserAccountRoles.ADMIN)
  }

  const user = auth.authenticatedUser
  if (user?.is_superuser || user?.is_zuno_engineer) return false
  const company = companies.companies?.find(({ id }) => Number(id) === Number(companies.companyId))
  return company?.is_member_admin
})

const isCompanyAdminOrUserManager = computed(() => {
  const { userSettings } = useNuxtApp().$store
  const userRoles = userSettings.userRoles
  return userRoles.includes(UserAccountRoles.ADMIN) || userRoles.includes(UserAccountRoles.USER_MANAGER)
})

const { canViewCompanies, canViewMyAssignments, canViewEsgDataEntry, canViewGhgDataApproval, canViewGhgProgressTracker, canViewGhgDataEntry, canViewIntegration, canViewGhgEmissionFactorConfig, canViewDataAssignment, canViewEsgMetricsConfig, canViewAssets } = useMenuPermissions()

const reportingLinks = computed(() => [
  { title: 'GHG Summary', url: '/dashboard/reporting/quantitative-report', hidden: isManageUsersEnabled() },
  isReportingSideAppEnabled()
    ? { title: 'Report Generation', url: '/reporting/reports', external: true }
    : { title: 'Report Generation', url: '/dashboard/reporting/report-frameworks' },
])

const handleSidebarDropDown = (permType: string, newStatus: boolean) => {
  // Reset all sideBarMenuList to false
  Object.keys(sideBarMenuList).forEach((key) => {
    sideBarMenuList[key] = false
  })
  // Add or update the specified menu type with new value
  sideBarMenuList[permType] = newStatus
}

const menuItems = computed(() => [
  { title: 'Assets', url: '/dashboard/assets', hidden: !canViewAssets.value },
  {
    title: isEsgRevampedEnabled() ? 'GHG Data Configuration' : 'Metrics Selection',
    url: '/dashboard/esg-metrics',
    hidden: !isCompanyAdmin.value || isGhgAssignmentRevampEnabled(),
  },
  {
    title: 'ESG Metrics Configuration',
    url: '/dashboard/esg-metrics-configuration',
    hidden: !isEsgRevampedEnabled() || !canViewEsgMetricsConfig.value,
  },
  {
    title: isNewUserAssignmentEnabled() ? 'Data Assignment' : 'GHG Data Assignment',
    url: '/dashboard/data-assignment',
    hidden: !canViewDataAssignment.value,
  },

  {
    title: 'GHG EF Configuration',
    url: '/dashboard/ghg-emission-factor-configuration',
    hidden: !canViewGhgEmissionFactorConfig.value,
  },
  {
    title: 'Integration',
    url: '/dashboard/settings/integration',
    hidden: !canViewIntegration.value,
  },
])
</script>
